import {Suspense, useCallback, useEffect, useRef, useState, type Ref} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {PencilIcon} from '@primer/octicons-react'
import {Heading, IconButton, PageHeader} from '@primer/react'
import {useMediaQuery} from '@github-ui/use-media-query'
import {useLayoutQuery} from '../page-data/loaders/use-layout-query'
import {PullRequestBanners} from './PullRequestBanners'
import {FilesChangedFeedback} from './FilesChangedFeedback'
import {PullRequestHeaderSummary} from './PullRequestHeaderSummary'
import {PullRequestEditTitleForm} from './PullRequestEditTitleForm'
import {PullRequestStateLabel} from './PullRequestStateLabel'
import {StackState} from './StackState'
import {StackNavigationCommands} from './StackNavigationCommands'
import type {AliveChannels, HeaderPageData} from '../page-data/payloads/header'
import {FeatureState} from '../page-data/payloads/header'
import {PullRequestHeaderNavigation} from './PullRequestHeaderNavigation'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {PullRequestCodeButton} from './PullRequestCodeButton'
import {DiffStat, DiffStatLoadingSkeleton} from './DiffStat'
import {clsx} from 'clsx'
import styles from './PullRequestHeader.module.css'
import {isLoggedIn} from '@github-ui/client-env'
import {MergeStatusButtonWrapper} from '@github-ui/mergebox'
import {isPullRequestOpenState} from '../utils/pull-request-state'
import {StickyPullRequestHeader, type StickyPullRequestHeaderProps} from './StickyPullRequestHeader'
import {setTitle} from '@github-ui/document-metadata'
import {PullRequestsTopBanners} from './banners/PullRequestsTopBanners'
import {
  DockedPanel,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
  useDockedPanelActions,
} from '../contexts/DockedPanelContext'
import {useMergeStatusButtonData} from '../hooks/use-merge-status-button-data'
import type {PullRequestActiveRoute} from '../hooks/use-active-route'
import {getRouteConfig} from '../routes/route-manager'
import {OverviewFeatureToggle, useOverviewToggleRenderState} from './overview/feature-preview/OverviewFeatureToggle'
import {useFeatureState, useIsOverviewEnabled} from '../hooks/use-feature-state'
import {useUpdateTitleMutation} from '../mutations/use-update-title-mutation'
import {announce} from '@github-ui/aria-live'
import {SubscribeButton} from './SubscribeButton'

/**  HelpUrl is omitted to move away from prop drilling route, page, and query data
See: https://github.com/github/github-ui/blob/main/.github/instructions/pull-requests-overview.instructions.md */
export type PullRequestHeaderProps = Omit<HeaderPageData, 'helpUrl'> & {
  activeRoute: PullRequestActiveRoute
  stickyHeaderVariant?: StickyPullRequestHeaderProps['pageVariant']
  aliveChannels: AliveChannels
  navigationRef?: Ref<HTMLDivElement>
}

export function PullRequestHeader({
  aliveChannels,
  bannersData: initialBannersData,
  activeRoute,
  featureStates,
  stickyHeaderVariant,
  pullRequest: initialPullRequest,
  pageTitle: initialPageTitle,
  repository: initialRepository,
  urls: initialUrls,
  user: initialUser,
  mergeStatusButtonData: initialMergeStatusButtonData,
  stack: initialStack,
  viewSettings,
  navigationRef,
}: PullRequestHeaderProps) {
  const {helpUrl} = useLayoutQuery().data

  const {data: headerData} = useLayoutQuery({
    aliveChannels,
    bannersData: initialBannersData,
    featureStates,
    helpUrl,
    pageTitle: initialPageTitle,
    pullRequest: initialPullRequest,
    repository: initialRepository,
    stack: initialStack,
    urls: initialUrls,
    user: initialUser,
    mergeStatusButtonData: initialMergeStatusButtonData,
    viewSettings,
  })
  const {bannersData, pullRequest, repository, pageTitle, urls, user, mergeStatusButtonData, stack} = headerData

  /** The title the PR had when editing began. */
  const [initialTitle, setInitialTitle] = useState(pullRequest.title)
  /** The current value of the title edit field. Undefined when not editing. */
  const [draftTitle, setDraftTitle] = useState<string>()
  const isEditingTitle = draftTitle !== undefined
  const updateTitleMutation = useUpdateTitleMutation()

  const startEditingTitle = () => {
    setInitialTitle(pullRequest.title)
    setDraftTitle(pullRequest.title)
  }

  const submitEditTitle = () => {
    if (!isEditingTitle) return

    setDraftTitle(undefined)
    updateTitleMutation.mutate(
      {id: pullRequest.number, title: draftTitle, expectedTitle: initialTitle},
      {
        onError: (error, variables) => {
          announce(error.message)
          setDraftTitle(variables.title) // revert back to edit mode, restoring submitted value
        },
      },
    )
  }

  const cancelEditTitle = () => {
    setDraftTitle(undefined)
    updateTitleMutation.reset()
  }

  const titleRef = useRef<HTMLHeadingElement>(null)
  const showUpdatedHeader = useIsOverviewEnabled()
  const {dockedPanelSupported} = getRouteConfig(activeRoute, showUpdatedHeader)
  const navigationShortcutsEnabled = isFeatureEnabled('pull_request_stacks_navigation_shortcuts')

  const shouldShowLoggedInOptions = isLoggedIn()
  const isPullRequestStacksEnabled = useFeatureState('pullRequestStacks') === FeatureState.Enabled
  const isPROpen = isPullRequestOpenState(pullRequest.state)
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  const hasValidatedMergeData = validatedMergeData !== null

  const sidePanelOpen = useIsDockedPanelOpen()
  const isPanelCompact = useIsDockedPanelCompact()
  const panelOpenOnDesktop = sidePanelOpen && !isPanelCompact

  // Docked merge status panel — only active on files/changes routes
  const isDockedMode = !!dockedPanelSupported
  const dockedPanelActions = useDockedPanelActions()
  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)

  const handleToggleDockedMergePanel = useCallback(() => {
    dockedPanelActions.togglePanel(DockedPanel.MergeStatus, {returnFocusTo: mergeStatusButtonRef.current})
  }, [dockedPanelActions])

  const isMergeStatusPanelOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)

  // Auto-close the docked merge status panel when the PR is merged or closed.
  // After merge, validatedMergeData becomes null which unmounts MergeStatusButton
  useEffect(() => {
    if (isDockedMode && isMergeStatusPanelOpen && !hasValidatedMergeData) {
      dockedPanelActions.closePanel()
    }
  }, [isDockedMode, isMergeStatusPanelOpen, hasValidatedMergeData, dockedPanelActions])

  useEffect(() => {
    if (pageTitle) {
      setTitle(pageTitle)
    }
  }, [pageTitle])

  // Primer's narrow viewport range
  const feedbackRenderLocation: 'mobile' | 'desktop' = useMediaQuery('(max-width: calc(48rem - 0.02px))')
    ? 'mobile'
    : 'desktop'

  const isFilesChangedRoute = activeRoute === 'files_changed'
  const renderOverviewFeatureToggle = useOverviewToggleRenderState() !== 'none'
  const renderOldHeaderLoggedInOptions = shouldShowLoggedInOptions && !showUpdatedHeader
  const renderUpdatedHeaderLoggedInOptions = shouldShowLoggedInOptions && showUpdatedHeader
  const hasActionsContent =
    isFilesChangedRoute ||
    renderOldHeaderLoggedInOptions ||
    renderUpdatedHeaderLoggedInOptions ||
    renderOverviewFeatureToggle

  const statusIndicators = (
    <div className="d-flex flex-items-center gap-2 flex-shrink-0">
      <PullRequestStateLabel state={pullRequest.state} fallbackFocusRef={titleRef} />
      <StackState pullRequestNumber={pullRequest.number} stack={stack} viewerCanUnstack={user.canEditTitle} />
    </div>
  )

  const previewAction = renderOverviewFeatureToggle && <OverviewFeatureToggle key="preview" />
  const codeAction = renderUpdatedHeaderLoggedInOptions && (
    <div key="code" className="d-flex gap-2">
      <PullRequestCodeButton
        codespacesEnabled={repository.codespacesEnabled}
        headBranch={pullRequest.headBranch}
        isEnterprise={repository.isEnterprise}
        pullRequestId={pullRequest.id}
        pullRequestNumber={pullRequest.number}
        repository={repository}
        size="medium"
        trailingVisual
        iconOnly
      />
      <SubscribeButton />
    </div>
  )

  return (
    <>
      {isPullRequestStacksEnabled && navigationShortcutsEnabled && stack && (
        <StackNavigationCommands pullRequestNumber={pullRequest.number} stack={stack} />
      )}
      {/** Used by suggested-changes.ts to disable the "suggested changes" UI when the PR is closed */}
      <div className="js-pull-header-details" data-pull-is-open={isPROpen ? 'true' : 'false'} hidden />
      <PullRequestsTopBanners
        bannersData={bannersData}
        pullRequestId={pullRequest.id}
        pullRequestNumber={pullRequest.number}
        baseBranch={pullRequest.baseBranch}
      />
      {isEditingTitle && (
        <div
          className={clsx(
            showUpdatedHeader && styles.editTitleFormUpdatedHeader,
            showUpdatedHeader && sidePanelOpen && styles.editTitleFormSidePanelOpen,
            showUpdatedHeader && styles.constrainedHeader,
          )}
        >
          <PullRequestEditTitleForm
            value={draftTitle}
            isDirty={draftTitle !== initialTitle}
            onChange={setDraftTitle}
            onCancel={cancelEditTitle}
            onSubmit={submitEditTitle}
            submitError={updateTitleMutation.error}
          />
        </div>
      )}
      <div className={clsx(showUpdatedHeader && styles.headerBorderBottom)} data-side-panel-open={panelOpenOnDesktop}>
        <PageHeader
          className={clsx(
            'flex-items-center',
            showUpdatedHeader && styles.constrainedHeader,
            showUpdatedHeader && styles.headerContainer,
            showUpdatedHeader && !isEditingTitle && 'tmp-mt-3',
            sidePanelOpen && styles.sidePanelOpen,
            sidePanelOpen && showUpdatedHeader && styles.sidePanelOpenUpdatedHeader,
            styles.actionsAboveTitleOnNarrow,
            styles.actionsAboveTitleOnNarrowContainer,
          )}
        >
          {isEditingTitle && (
            // this is done so that we still have an h1 element present when editing the title
            <h1 ref={titleRef} tabIndex={showUpdatedHeader ? -1 : undefined} className="sr-only">
              {`${pullRequest.title} - #${pullRequest.number}`}
            </h1>
          )}
          {!isEditingTitle && (
            <>
              <PageHeader.TitleArea>
                <div className={styles.titleWithAction}>
                  <Heading
                    as="h1"
                    ref={titleRef}
                    tabIndex={showUpdatedHeader ? -1 : undefined}
                    className={clsx('lh-condensed', styles.inlineTitle, showUpdatedHeader && styles.title)}
                  >
                    <VerifiedHTMLText className="f1 text-normal markdown-title" html={pullRequest.titleHtml} />
                    <span className="sr-only"> - #{pullRequest.number}</span>
                  </Heading>
                  <span className={clsx(styles.titleSuffix, showUpdatedHeader && styles.inlineTitleSuffix)}>
                    {/* Keep the inline edit button attached to the title's final word. */}
                    {showUpdatedHeader && shouldShowLoggedInOptions && user.canEditTitle && (
                      <span aria-hidden="true">{'\u2060'}</span>
                    )}
                    {!showUpdatedHeader && (
                      <span aria-hidden="true" className="pl-2 fgColor-muted f1-light lh-condensed">
                        #{pullRequest.number}
                      </span>
                    )}
                    {shouldShowLoggedInOptions && user.canEditTitle && (
                      <IconButton
                        icon={PencilIcon}
                        onClick={startEditingTitle}
                        size="medium"
                        aria-label="Edit title"
                        variant="invisible"
                        className="ml-1"
                      />
                    )}
                  </span>
                </div>
              </PageHeader.TitleArea>
              <PageHeader.Actions
                className={clsx(
                  'flex-items-center gap-2 position-relative',
                  showUpdatedHeader && styles.updatedActions,
                  !hasActionsContent && 'd-none',
                )}
              >
                {isFilesChangedRoute && (
                  <FilesChangedFeedback repository={repository} urls={urls} renderLocation={feedbackRenderLocation} />
                )}
                {showUpdatedHeader && feedbackRenderLocation === 'mobile'
                  ? [codeAction, previewAction]
                  : [previewAction, codeAction]}
                {renderOldHeaderLoggedInOptions && (
                  <div className="d-flex gap-2">
                    {validatedMergeData && (
                      <MergeStatusButtonWrapper
                        channels={validatedMergeData.channels}
                        headSha={validatedMergeData.headSha}
                        helpUrl={helpUrl}
                        viewerLogin={validatedMergeData.viewerLogin}
                        isDockedMode={isDockedMode}
                        onToggleDockedPanel={handleToggleDockedMergePanel}
                        mergeStatusButtonRef={mergeStatusButtonRef}
                      />
                    )}
                    <PullRequestCodeButton
                      codespacesEnabled={repository.codespacesEnabled}
                      headBranch={pullRequest.headBranch}
                      isEnterprise={repository.isEnterprise}
                      pullRequestId={pullRequest.id}
                      pullRequestNumber={pullRequest.number}
                      repository={repository}
                      size={'medium'}
                    />
                  </div>
                )}
              </PageHeader.Actions>
            </>
          )}
          <PageHeader.Description className={'d-flex flex-column flex-items-start'}>
            <div
              className={clsx(
                'd-flex width-full',
                showUpdatedHeader
                  ? 'flex-items-center gap-2 flex-wrap min-width-0'
                  : 'flex-items-start gap-2 flex-column flex-sm-row flex-justify-between',
              )}
            >
              {!showUpdatedHeader && statusIndicators}
              <div className={showUpdatedHeader ? styles.summary : 'flex-1 overflow-hidden'}>
                <PullRequestHeaderSummary
                  leadingContent={showUpdatedHeader ? statusIndicators : undefined}
                  author={pullRequest.author}
                  attributedUser={pullRequest.attributedUser}
                  mergedTime={pullRequest.mergedTime}
                  baseBranch={pullRequest.baseBranch}
                  baseRepositoryDefaultBranch={repository.defaultBranch}
                  baseRepositoryName={repository.name}
                  baseRepositoryOwnerLogin={repository.ownerLogin}
                  canChangeBase={user.canChangeBase}
                  commitsCount={pullRequest.commitsCount}
                  headBranch={pullRequest.headBranch}
                  headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                  headRepositoryName={pullRequest.headRepositoryName}
                  isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                  isEditing={isEditingTitle}
                  mergedBy={pullRequest.mergedBy}
                  mergedByAvatarUrl={pullRequest.mergedByAvatarUrl}
                  mergedByName={pullRequest.mergedByName}
                  repositoryId={repository.id}
                  pullRequestId={pullRequest.id}
                  pullRequestNumber={pullRequest.number}
                  setIsEditing={isEditing => (isEditing ? startEditingTitle() : cancelEditTitle())}
                  state={pullRequest.state}
                  stack={stack}
                />
              </div>
            </div>
            <PullRequestBanners bannersData={bannersData} pullRequest={pullRequest} repository={repository} />
          </PageHeader.Description>
          <PageHeader.Navigation className="tmp-pt-3">
            <div
              className={clsx(
                styles.rightContentWrapper,
                !showUpdatedHeader && styles.hideWhenVeryNarrowContainer,
                showUpdatedHeader && styles.reducedTopPadding,
              )}
            >
              {!showUpdatedHeader && (
                <ErrorBoundary boundaryName="PullRequestHeaderDiffStat" fallback={null}>
                  <Suspense fallback={<DiffStatLoadingSkeleton />}>
                    <DiffStat />
                  </Suspense>
                </ErrorBoundary>
              )}
            </div>
            <div className="flex-auto" ref={navigationRef}>
              <PullRequestHeaderNavigation
                activeRoute={activeRoute}
                pullRequest={pullRequest}
                repository={repository}
                urls={urls}
              />
            </div>
          </PageHeader.Navigation>
        </PageHeader>
      </div>
      {!showUpdatedHeader && !isFilesChangedRoute && (
        <StickyPullRequestHeader
          viewerCanUnstack={user.canEditTitle}
          repository={repository}
          pullRequest={pullRequest}
          pageVariant={stickyHeaderVariant ?? 'condensed'}
          stack={stack}
        />
      )}
    </>
  )
}
